import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageWrapper from '../../../components/pageComponents/PageWrapper';
import ModalEnabled from '../../../components/modalComponents/ModalEnabled';
import CompletarClaseModeloModal from '../modales/CompletarClaseModeloModal';
import { useManageGetRequest } from '../../../hooks/useManageGetRequest/useManageGetRequest';
import { getClasesModeloPorEvaluarAPI } from '../../../consumers/backendApisUrls';
import {
    ContentWrapper, SectionTitle, SectionDescription, CardsList, ConvocatoriaCard,
    IconContainer, CardInformation, CardTitle, ProgressDetails, ApplyButton, EmptyState,
} from './shared';

// ──────────────────────────────────────────────────────────────────────────
// Pestaña "Clases modelo por completar" de "Mis Traslados y Promociones"
// (28/09/2026, a pedido de Jody). Mismo patrón que "Entrevistas por completar"
// (EntrevistasPorEvaluar.jsx): postulantes que el empleado logueado tiene
// asignados como evaluador de clase modelo (TP_ClaseModelo.IdEmpleadoEvaluador),
// pendientes de calificar. Trae GetClasesModeloPorEvaluar, que en Java filtra
// además por IdEstadoPostulacion=Configurado, IdEtapaPostulante=Clase Modelo y
// que la rúbrica ya esté cargada (Archivo.Discriminator='ClaseModeloRubricaArchivo')
// — ver Sql.PostulanteSQL.GET_CLASES_MODELO_POR_EVALUAR en intranet-puestos-backend.
//
// Igual que en EntrevistasPorEvaluar.jsx, el JSON viaja en PascalCase
// (ClaseModeloPorEvaluarViewModel es un ViewModel fuertemente tipado del lado .NET).
//
// El botón "Completar clase modelo" abre CompletarClaseModeloModal.jsx
// (28/09/2026, a pedido de Jody, sobre la captura de ResultadoClaseModelo.jsx
// que adjuntó): mismo patrón ModalEnabled que handleCompletarEntrevista en
// EntrevistasPorEvaluar.jsx. La fila completa (claseModelo) ya trae todo lo
// que el modal necesita — incluyendo IdPostulante/Dni/SedeEvaluadora/SedeOrigen,
// agregados a GetClasesModeloPorEvaluar específicamente para este modal.
// ──────────────────────────────────────────────────────────────────────────

const ClasesModeloPorCompletar = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [clasesModelo, setClasesModelo] = useState([]);
    const [modalEnabled, setModalEnabled] = useState({ isEnable: false });

    const [manageGetRequest] = useManageGetRequest();

    const successCallback = ({ data }) => {
        setClasesModelo(data ?? []);
    };

    const cargar = async () => {
        setIsLoading(true);
        try {
            await manageGetRequest(getClasesModeloPorEvaluarAPI(), successCallback, false);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCompletarClaseModelo = (claseModelo) => {
        setModalEnabled({ isEnable: true, component: CompletarClaseModeloModal, data: { claseModelo } });
    };

    return (
        <PageWrapper isLoading={isLoading}>
            <ContentWrapper>
                <SectionTitle>Clases modelo por completar</SectionTitle>
                <SectionDescription>Postulantes que tienes asignados como evaluador de clase modelo, pendientes de calificar</SectionDescription>

                <CardsList>
                    {clasesModelo.map(claseModelo => (
                        <ConvocatoriaCard key={claseModelo.IdClaseModelo}>
                            <IconContainer>
                                <FontAwesomeIcon icon="chalkboard-teacher" />
                            </IconContainer>

                            <CardInformation>
                                <CardTitle>{claseModelo.NombrePostulante}</CardTitle>
                                <ProgressDetails>
                                    <span>Evaluador: <strong>{claseModelo.NombreEvaluador}</strong></span>
                                    <span>Cargo: <strong>{claseModelo.CargoPostulante}</strong></span>
                                    <span>Sede: <strong>{claseModelo.SedePostulante}</strong></span>
                                    <span>Fecha límite: <strong>{claseModelo.FechaLimite}</strong></span>
                                </ProgressDetails>
                            </CardInformation>

                            <ApplyButton type="button" onClick={() => handleCompletarClaseModelo(claseModelo)}>
                                <FontAwesomeIcon icon="comment-dots" className="mr-1" />
                                Completar clase modelo
                            </ApplyButton>
                        </ConvocatoriaCard>
                    ))}
                </CardsList>

                {!isLoading && clasesModelo.length === 0 && (
                    <EmptyState>No tienes clases modelo pendientes por completar.</EmptyState>
                )}
            </ContentWrapper>

            <ModalEnabled modalEnabled={modalEnabled} setModalEnabled={setModalEnabled} refreshPage={cargar} />
        </PageWrapper>
    );
};

export default ClasesModeloPorCompletar;
